import { ActionIcon, Group, Stack } from '@mantine/core';
import { IconTrash } from '@tabler/icons-react';
import type { ComponentProps, ReactNode } from 'react';
import {
    type ArrayPath,
    type Control,
    type FieldArrayPathValue,
    useFieldArray,
} from 'react-hook-form';
import type { ExtractStringArrayKeys } from '../../../types/extractKeysOfType';
import { Inputs } from '../../../utils/form/inputs';
import { CollapsibleSection } from '../../data-display/CollapsibleSection';
import { Section } from '../../data-display/Section';

// TODO nullable -> name
//  should we set value to null when array is empty?
export const Form_FieldArrayStringSection = <T extends object>({
    name,
    control,
    readOnly,
    disabled,
    initialOpen,
    label,
    handleExpansionChange,
    notCollapsible,
    onAddDefaultValue = () => '',
}: {
    name: (ExtractStringArrayKeys<T, false> | ExtractStringArrayKeys<T, true>) &
        string;
    control: Control<T>;
    initialOpen: boolean | undefined;
    disabled: boolean;
    readOnly: boolean;
    notCollapsible: boolean;
    label: ReactNode;
    onAddDefaultValue?: () => string;
    handleExpansionChange: (value: boolean) => void;
}) => {
    const { fields, append, remove } = useFieldArray({
        control,
        name: name as ArrayPath<T>,
    });

    const defaultValue = onAddDefaultValue() as FieldArrayPathValue<
        T,
        ArrayPath<T>
    >;

    const content = (
        <Stack>
            {fields.map((field, index) => (
                <Group
                    key={field.id}
                    wrap={'nowrap'}
                >
                    <Inputs.String
                        nullable={false}
                        readOnly={readOnly}
                        control={control}
                        name={
                            `${name}.${index}` as ComponentProps<
                                typeof Inputs.String<T, false>
                            >['name']
                        }
                        style={{ flexGrow: 1 }}
                    />
                    {!readOnly && (
                        <ActionIcon
                            size={'sm'}
                            variant={'transparent'}
                            color={'var(--mantine-color-base-button)'}
                            onClick={() => remove(index)}
                        >
                            <IconTrash />
                        </ActionIcon>
                    )}
                </Group>
            ))}
        </Stack>
    );

    if (fields.length === 0) {
        return (
            <Section
                label={label}
                withoutBackgroundColor={!readOnly}
                disabled={disabled}
                handleAddClick={
                    !readOnly ? () => append(defaultValue) : undefined
                }
            />
        );
    }

    if (notCollapsible) {
        return (
            <Section
                withoutBackgroundColor={!readOnly}
                label={label}
                handleAddClick={
                    !readOnly ? () => append(defaultValue) : undefined
                }
                disabled={disabled}
            >
                {content}
            </Section>
        );
    }

    return (
        <CollapsibleSection
            withoutBackgroundColor={!readOnly}
            label={label}
            handleAddClick={!readOnly ? () => append(defaultValue) : undefined}
            initialOpen={initialOpen}
            disabled={disabled}
            handleExpansionChange={handleExpansionChange}
        >
            {content}
        </CollapsibleSection>
    );
};
